import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import ReactDOM from "react-dom";
import {
  ArrowPathIcon,
  ArrowUpRightIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { useSidePeek } from "../../../hooks/useSidePeek";
import { ExcalidrawMindmapNodeElement } from "../../../../packages/excalidraw/element/types";
import { NotionIcon } from "../../../../packages/excalidraw/components/icons";
import {
  MIN_MINDMAP_PILL_WIDTH,
  MINDMAP_PILL_BORDER_WIDTH,
  MINDMAP_PILL_GLYPH_GAP,
  MINDMAP_PILL_GLYPH_SIZE,
  MINDMAP_PILL_LINE_HEIGHT,
  MINDMAP_PILL_PADDING,
  getMindmapPillBorderColor,
  getMindmapPillFill,
  getMindmapPillTextColor,
  isMindmapRootPill,
  mindmapPillClampLines,
} from "../../../../packages/excalidraw/element/mindmapPillStyle";
import { SidePeekOpenIcon } from "../../../assets/SidePeekOpenIcon";
import {
  NotionIconElementType,
  processNotionIcon,
} from "../../../helper/canvasUtils";
import {
  dispatchCanvasEvent,
  subscribeEvent,
  unSubscribeEvent,
} from "../../../../packages/excalidraw/events";
import { t } from "../../../../packages/excalidraw/i18n";
import { AppState, SceneData } from "../../../../packages/excalidraw/types";
import { CHIP_ALLOWANCE } from "../../../../packages/mindmap-core/src/mindmapLayout";
import { isGenerationPlaceholder } from "../../../data/mindmapGenerate";
import type { MindmapCustomData } from "../../../../packages/mindmap-core/src/mindmapElements";
import {
  isExpandInFlight,
  subscribeExpandInFlight,
} from "../../../data/mindmapExpandBusy";
import {
  isRefreshInFlight,
  subscribeRefreshInFlight,
} from "../../../data/mindmapRefreshBusy";
import { loadMindmapGroupingCandidates } from "../../../data/mindmapRegroup";
import type { Grouping } from "../../../../packages/mindmap-core/src/mindmapDbTree";

/**
 * Payload of the `mindmapToggleExpand` canvas event dispatched when a node's
 * expand/collapse chip is clicked. The app shell (#51) subscribes and runs the
 * progressive-disclosure expand/collapse against the current scene.
 */
export interface MindmapToggleExpandDetail {
  elementId: string;
}

interface Props {
  element: ExcalidrawMindmapNodeElement;
  parentStyle: React.CSSProperties;
  /**
   * Read-only surface (a shared scene, a Notion embed, or a locked element).
   * The chip forces `pointerEvents: "auto"` and the hover toolbar portals to
   * `document.body`, so neither is stopped by the canvas container's disabled
   * pointer events — they have to be withheld here or a viewer can expand,
   * collapse and refresh someone else's map.
   */
  viewModeEnabled?: boolean;
  updateScene?: <K extends keyof AppState>(sceneData: {
    elements?: SceneData["elements"];
    appState?: Pick<AppState, K> | null | undefined;
    collaborators?: SceneData["collaborators"];
    commitToHistory?: SceneData["commitToHistory"];
  }) => void;
}

/** Diameter of the round expand/collapse chip (Fix 1): a comfortable hit
 * target seated in the element's reserved CHIP_ALLOWANCE gap. */
const CHIP_SIZE = 20;

/**
 * Keyframes for the spinners this renderer shows. Injected once at module load
 * rather than as a `<style>` element per pill: a map puts one of these
 * components on the canvas per node, and only busy pills ever animate.
 *
 * The generation placeholder used to pulse its whole opacity between 1 and .5.
 * With the canvas twin painting underneath (V3 P4) that let a second, offset
 * copy of the label show through on every dip, so the pulse is gone: the
 * placeholder is a steady 65% plate with a spinner, which also sidesteps the
 * reduced-motion objection to a full-pill animation.
 */
const SPIN_STYLE_ID = "mindmap-node-spin-style";
if (
  typeof document !== "undefined" &&
  !document.getElementById(SPIN_STYLE_ID)
) {
  const style = document.createElement("style");
  style.id = SPIN_STYLE_ID;
  style.textContent = [
    "@keyframes mindmap-node-spin{to{transform:rotate(360deg)}}",
    ".mindmap-node-spinner{animation:mindmap-node-spin 0.9s linear infinite}",
  ].join("");
  document.head.appendChild(style);
}

/** Opacity a generation placeholder's pill holds while its map is building. */
const PLACEHOLDER_OPACITY = 0.65;

/** Hit target of a hover-toolbar button. */
const OVERLAY_BUTTON_SIZE = 28;

/** Gap between the pill's top edge and the floating toolbar above it. */
const OVERLAY_OFFSET = 6;

/** The fixed square every leading pill glyph occupies. Fixed rather than
 * intrinsic because `mindmapLabelBoxWidth` charges the label for exactly this. */
const glyphBoxStyle: React.CSSProperties = {
  width: MINDMAP_PILL_GLYPH_SIZE,
  height: MINDMAP_PILL_GLYPH_SIZE,
  flex: "0 0 auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

/** The one spinner mark: a track ring plus a quarter arc, drawn in
 * `currentColor` so each call site (chip, placeholder, toolbar) tints it. */
const Spinner: React.FC<{ size: number; testId: string }> = ({
  size,
  testId,
}) => (
  <svg
    data-testid={testId}
    className="mindmap-node-spinner animate-spin"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      stroke="currentColor"
      strokeOpacity="0.35"
      strokeWidth="3"
    />
    <path
      d="M21 12a9 9 0 0 0-9-9"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Whether the spacebar is currently held. Excalidraw tracks this internally
 * (`isHoldingSpace` in App.tsx) but does not export it, and the chip must let
 * space-drag pan gestures through instead of swallowing their pointerdown.
 */
let spaceHeld = false;
if (typeof window !== "undefined") {
  window.addEventListener("keydown", (event) => {
    if (event.key === " ") {
      spaceHeld = true;
    }
  });
  window.addEventListener("keyup", (event) => {
    if (event.key === " ") {
      spaceHeld = false;
    }
  });
  window.addEventListener("blur", () => {
    spaceHeld = false;
  });
}

/** App.tsx paints the integration container white in dark theme and black in
 * light theme; that is the only theme signal reaching this renderer. */
const isDarkParentStyle = (parentStyle: React.CSSProperties): boolean =>
  typeof parentStyle.color === "string" &&
  parentStyle.color.trim().toLowerCase() === "white";

/** A chevron pointing right (collapsed: "show children") or left (expanded). */
const ChipChevron: React.FC<{ direction: "left" | "right" }> = ({
  direction,
}) => (
  <svg
    width={10}
    height={10}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{
      transform: direction === "left" ? "rotate(180deg)" : undefined,
    }}
  >
    <path
      d="M9 5l7 7-7 7"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RenderMindmapNodeInner: React.FC<Props> = ({
  element,
  parentStyle,
  updateScene,
  viewModeEnabled,
}) => {
  const { setPageId, setShow, show, setPublicUrl, setUpdateElement } =
    useSidePeek();
  const [isHovered, setIsHovered] = useState(false);
  const pillRef = useRef<HTMLDivElement | null>(null);
  const [anchor, setAnchor] = useState<{
    top: number;
    right: number;
    bottom: number;
  } | null>(null);
  // The Group by popover, and the candidates it fetched for itself. `null` means
  // "not fetched yet", so the popover can show a loading line instead of
  // pretending a database has no groupable properties — but a map that carries
  // its candidate list never gets here at all.
  const [groupOpen, setGroupOpen] = useState(false);
  const [fetchedGroupings, setFetchedGroupings] = useState<Grouping[] | null>(
    null,
  );
  // A failed query leaves `fetchedGroupings` null forever, which used to read as
  // "still loading" and hung the popover on its spinner line for good.
  const [groupingFailed, setGroupingFailed] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  // The toolbar floats in a gap ABOVE the pill, so the pointer leaves the node
  // before it reaches the card. A short grace period bridges that gap; entering
  // the card cancels it, leaving anywhere else lets it close.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const openOverlay = useCallback(() => {
    clearCloseTimer();
    setIsHovered(true);
  }, [clearCloseTimer]);
  const closeOverlay = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setIsHovered(false), 160);
  }, [clearCloseTimer]);

  // Whether this node's expand fetch is in flight, so the chip shows an in-place
  // busy state (Fix 2). Subscribes to the shared store — the fetch does not
  // touch the scene, so nothing else would re-render the chip.
  const busy = useSyncExternalStore(
    subscribeExpandInFlight,
    () => isExpandInFlight(element.id),
    () => false,
  );

  const mindmap = element.customData?.mindmap as MindmapCustomData | undefined;
  const mapId = mindmap?.mapId || "";

  // Whether this map's refresh is running, so the toolbar's refresh button can
  // spin and disable itself (V3 P7). Keyed by map, not element: refresh re-walks
  // the whole map, and only the root pill offers the button anyway.
  const refreshing = useSyncExternalStore(
    subscribeRefreshInFlight,
    () => (mapId ? isRefreshInFlight(mapId) : false),
    () => false,
  );

  const isDark = isDarkParentStyle(parentStyle);
  const isRoot = isMindmapRootPill(element);
  const isPlaceholder = isGenerationPlaceholder(element);
  const height = element.height;
  // An expandable node reserves CHIP_ALLOWANCE on its trailing edge (Fix 1) so
  // the round chip renders fully inside the element box and the integration
  // container's `overflow: hidden` can't clip it. The pill itself is drawn in
  // the remaining width; the chip sits in the reserved gap.
  // Reserve the gap only when the chip actually renders. Connectors attach at
  // the element box's edge, so holding the space back with no chip in it left a
  // 28px hole between every pill and its line — why a /share map (chip hidden,
  // read-only) read as disconnected while the editor looked fine.
  const showsChip = element.expandable && !viewModeEnabled && !element.locked;
  // Refresh and regroup both report on the ROOT pill's chip, not only inside the
  // hover toolbar that started them: the toolbar (and the Group by popover with
  // it) closes the moment the pointer leaves, so the spinner it owns was
  // invisible for all but the first instant of a multi-second run. The chip is
  // always on canvas.
  const chipBusy = busy || (isRoot && refreshing);
  const chipGap = showsChip ? CHIP_ALLOWANCE : 0;
  const width = Math.max(MIN_MINDMAP_PILL_WIDTH, element.width - chipGap);

  // V3: a flat solid plate, not a roughjs sketch. The hand-drawn outline read as
  // noise once maps grew past a dozen nodes and cost a rough re-generation on
  // every width/theme change; a plain bordered div is both calmer and free.
  const accentColor = getMindmapPillBorderColor(element.branchColor, isRoot);
  const pillFill = getMindmapPillFill(element.branchColor, isDark, isRoot);

  const showSidePeek = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (element.nodeKind !== "page") {
      return;
    }
    if (show === false) {
      setShow(true);
    }
    updateScene && setUpdateElement({ scene: updateScene });
    setPageId(element.pageId || "");
    setPublicUrl("");
  };

  const toggleExpand = (event: React.MouseEvent) => {
    // The chip is its own click target: don't let the click bubble to the canvas.
    event.stopPropagation();
    const detail: MindmapToggleExpandDetail = { elementId: element.id };
    dispatchCanvasEvent("mindmapToggleExpand", detail);
  };

  // Refresh from the root pill's hover overlay — the same canvas event the
  // context-menu action dispatches, so the shell runs one refresh pipeline. The
  // context menu was the only entry point and nobody found it (V3).
  const refreshMap = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (refreshing) {
      return;
    }
    if (mindmap?.mapId && mindmap?.rootPageId) {
      dispatchCanvasEvent("refreshMindmapFromNotion", {
        mapId: mindmap.mapId,
        rootPageId: mindmap.rootPageId,
      });
    }
  };

  // Regroup this database map. Restamping + refetching both need the app shell,
  // so — like refresh — hand off via a canvas event rather than reaching for the
  // Notion layer from a DOM renderer. `undefined` means "None (flat)".
  const chooseGrouping = (groupingProperty?: string) => {
    setGroupOpen(false);
    setIsHovered(false);
    if (
      !mindmap?.mapId ||
      !mindmap.rootPageId ||
      mindmap.groupingProperty === groupingProperty
    ) {
      return;
    }
    dispatchCanvasEvent("mindmapRegroup", {
      mapId: mindmap.mapId,
      rootPageId: mindmap.rootPageId,
      groupingProperty,
    });
  };

  const onChipPointerDown = (event: React.PointerEvent) => {
    // Swallow only a plain left-press, which would otherwise start a drag of the
    // element under the chip. Middle-button and space+drag are pan gestures and
    // must reach the canvas, or the chip becomes a dead zone for panning.
    if (!event.button && !spaceHeld) {
      event.stopPropagation();
    }
  };

  // Only page nodes have something to open — block/database kinds get no
  // open/side-peek pair; the root gets refresh whatever its kind is.
  // Every affordance is withheld on a read-only surface: the toolbar mutates
  // (refresh) or navigates out, and the chip expands/collapses the map.
  const interactive = !viewModeEnabled && !element.locked;
  const hasSidePeek = interactive && element.nodeKind === "page";
  // Only a DATABASE map's root can be regrouped: a page map has no properties,
  // and a row or group child is not the thing being organised.
  const canRegroup = interactive && isRoot && mindmap?.source === "database";
  const hasOverlay = hasSidePeek || (interactive && isRoot);
  const notionUrl = `https://notion.so/${(element.pageId || "").replace(
    /-/g,
    "",
  )}`;
  // Separates the chip from the pill/canvas behind it.
  const ringColor = "var(--island-bg-color, white)";
  const overlayButtonStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: OVERLAY_BUTTON_SIZE,
    height: OVERLAY_BUTTON_SIZE,
    padding: 0,
    borderRadius: 6,
    border: "none",
    background: "transparent",
    color: "inherit",
    cursor: "pointer",
  };

  // An open popover pins the toolbar: the pointer has to leave the pill to
  // reach it, and the toolbar vanishing under the pointer took the popover too.
  const overlayOpen = hasOverlay && (isHovered || groupOpen);

  // The toolbar renders into `document.body`, so it needs the pill's viewport
  // rect to sit above it. Measured when the overlay opens; it only lives as long
  // as the hover, so there is nothing to keep in sync beyond that.
  useLayoutEffect(() => {
    if (!overlayOpen) {
      setAnchor(null);
      return;
    }
    const rect = pillRef.current?.getBoundingClientRect();
    if (rect) {
      setAnchor({ top: rect.top, right: rect.right, bottom: rect.bottom });
    }
  }, [overlayOpen, element.width, element.height]);

  // Any canvas pan or zoom moves the pill out from under a toolbar measured at
  // hover time. Rather than track it, drop the overlay — the pointer is still on
  // the canvas, so re-hovering re-measures.
  useEffect(() => {
    if (!overlayOpen) {
      return;
    }
    const hide = (event: Event) => {
      // A wheel that lands ON the toolbar or popover belongs to the swallowing
      // listener below, which cancels it. Tearing the overlay down from here
      // first would remove that listener before the event reached it — the
      // gesture would then fall through to the browser and zoom the page.
      // `instanceof Node` because a canvas wheel arrives targeted at `window`,
      // which `contains` rejects.
      const target = event.target;
      if (
        target instanceof Node &&
        (overlayRef.current?.contains(target) ||
          popoverRef.current?.contains(target))
      ) {
        return;
      }
      setIsHovered(false);
      setGroupOpen(false);
    };
    window.addEventListener("scroll", hide, true);
    window.addEventListener("wheel", hide, true);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("wheel", hide, true);
    };
  }, [overlayOpen]);

  // Both float in `document.body`, outside the canvas that registers the
  // non-passive wheel listener, so a Cmd+scroll landing on one of them reached
  // the browser and zoomed the whole PAGE. The window listener above cannot
  // prevent that (window wheel listeners are passive by default), so swallow the
  // gesture here and dismiss — the pointer is back over the canvas, where the
  // next scroll zooms the map as usual.
  useEffect(() => {
    if (!overlayOpen) {
      return;
    }
    const nodes = [overlayRef.current, popoverRef.current].filter(
      (node): node is HTMLDivElement => node !== null,
    );
    const swallow = (event: WheelEvent) => {
      event.preventDefault();
      setIsHovered(false);
      setGroupOpen(false);
    };
    for (const node of nodes) {
      node.addEventListener("wheel", swallow, { passive: false });
    }
    return () => {
      for (const node of nodes) {
        node.removeEventListener("wheel", swallow);
      }
    };
    // `anchor` is load-bearing, not incidental: neither portal is mounted until
    // it is measured, and it is measured in a layout effect during THIS commit —
    // so without it in the deps this ran once, against two null refs, and the
    // listeners were never attached at all.
  }, [overlayOpen, groupOpen, anchor]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  // Stored on the map at refresh time (and after this pill's own first fetch),
  // so re-opening the popover costs nothing — the owner's "why does it load
  // every time". A refresh rewrites it, so a property added in Notion appears.
  const groupings = mindmap?.groupingCandidates ?? fetchedGroupings;

  // Only a map with no stored list fetches, and only once: the result is written
  // back to the root pill, so the next open reads it from the scene.
  useEffect(() => {
    const databaseId = mindmap?.rootPageId;
    if (!groupOpen || groupings !== null || !databaseId || !mapId) {
      return;
    }
    let live = true;
    void loadMindmapGroupingCandidates(mapId, databaseId)
      .then((list) => {
        if (live) {
          setFetchedGroupings(list);
        }
      })
      .catch(() => {
        if (live) {
          setGroupingFailed(true);
        }
      });
    return () => {
      live = false;
      // Closing the popover clears the failure, so re-opening retries: a
      // one-off network blip must not disable Group by for the whole session.
      setGroupingFailed(false);
    };
  }, [groupOpen, groupings, mapId, mindmap?.rootPageId]);

  // A press anywhere but inside the popover dismisses it — without this the
  // pinned toolbar had no way to close short of choosing something.
  useEffect(() => {
    if (!groupOpen) {
      return;
    }
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // The toolbar counts as "inside" too. Checking only the popover meant a
      // press on Refresh / side peek / open-in-Notion closed the overlay on
      // mousedown, so the button was unmounted before its click arrived: with
      // the popover open, every other control was dead.
      if (
        popoverRef.current?.contains(target) ||
        overlayRef.current?.contains(target)
      ) {
        return;
      }
      setGroupOpen(false);
      setIsHovered(false);
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [groupOpen]);

  // The context-menu action can only reach this popover through the shell, so
  // it dispatches an open event addressed to this pill (mirroring "Expand all").
  useEffect(() => {
    if (!canRegroup) {
      return;
    }
    const onOpen = (event: CustomEvent) => {
      if (event.detail?.elementId === element.id) {
        setIsHovered(true);
        setGroupOpen(true);
      }
    };
    subscribeEvent("mindmapOpenGroupBy", onOpen);
    return () => unSubscribeEvent("mindmapOpenGroupBy", onOpen);
  }, [canRegroup, element.id]);

  const overlay = overlayOpen && anchor && (
    <div
      ref={overlayRef}
      data-testid="mindmap-node-actions"
      onPointerEnter={openOverlay}
      onPointerLeave={closeOverlay}
      style={{
        // Portalled out of the pill: the integration container clips its
        // children, so an in-pill overlay rendered as half-hidden icons (V3 P5).
        position: "fixed",
        left: anchor.right,
        top: anchor.top - OVERLAY_OFFSET,
        transform: "translate(-100%, -100%)",
        zIndex: 30,
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: 2,
        borderRadius: 8,
        border: `1px solid ${isDark ? "#3d3d43" : "#e5e5e8"}`,
        background: isDark ? "#232329" : "#ffffff",
        color: isDark ? "#e8e8e8" : "#1b1b1f",
        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.18)",
        // The canvas below must keep receiving pointer events; only the card
        // itself captures them.
        pointerEvents: "auto",
      }}
    >
      {hasSidePeek && (
        <>
          <a
            data-testid="mindmap-node-open-external"
            href={notionUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("mindmap.openInNotion")}
            title={t("mindmap.openInNotion")}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            style={overlayButtonStyle}
          >
            <ArrowUpRightIcon style={{ width: 16, height: 16 }} />
          </a>
          <button
            type="button"
            data-testid="mindmap-node-sidepeek"
            aria-label={t("mindmap.openSidePeek")}
            title={t("mindmap.openSidePeek")}
            onClick={showSidePeek}
            onPointerDown={(event) => event.stopPropagation()}
            style={overlayButtonStyle}
          >
            <SidePeekOpenIcon width={18} height={14} />
          </button>
        </>
      )}
      {canRegroup && (
        <button
          type="button"
          data-testid="mindmap-node-groupby"
          aria-label={t("mindmap.groupBy")}
          title={t("mindmap.groupBy")}
          aria-expanded={groupOpen}
          aria-haspopup="menu"
          disabled={refreshing}
          onClick={(event) => {
            event.stopPropagation();
            setGroupOpen((open) => !open);
          }}
          onPointerDown={(event) => event.stopPropagation()}
          style={{
            ...overlayButtonStyle,
            cursor: refreshing ? "default" : "pointer",
          }}
        >
          <Squares2X2Icon style={{ width: 16, height: 16 }} />
        </button>
      )}
      {isRoot && (
        <button
          type="button"
          data-testid="mindmap-node-refresh"
          aria-label={t("mindmap.refresh")}
          title={t("mindmap.refresh")}
          aria-busy={refreshing || undefined}
          disabled={refreshing}
          onClick={refreshMap}
          onPointerDown={(event) => event.stopPropagation()}
          style={{
            ...overlayButtonStyle,
            cursor: refreshing ? "default" : "pointer",
          }}
        >
          {refreshing ? (
            <Spinner size={16} testId="mindmap-refresh-spinner" />
          ) : (
            <ArrowPathIcon style={{ width: 16, height: 16 }} />
          )}
        </button>
      )}
    </div>
  );

  const groupOption = (
    label: string,
    testId: string,
    propertyName?: string,
  ) => {
    const selected = mindmap?.groupingProperty === propertyName;
    return (
      <button
        key={testId}
        type="button"
        role="menuitemradio"
        aria-checked={selected}
        data-testid={testId}
        onClick={(event) => {
          event.stopPropagation();
          chooseGrouping(propertyName);
        }}
        onPointerDown={(event) => event.stopPropagation()}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          width: "100%",
          padding: "6px 8px",
          borderRadius: 6,
          border: "none",
          background: selected
            ? isDark
              ? "#33333a"
              : "#f1f1f4"
            : "transparent",
          color: "inherit",
          font: "inherit",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
        {selected && <span aria-hidden="true">✓</span>}
      </button>
    );
  };

  // Its own portal rather than a child of the toolbar: the toolbar is a
  // one-line flex row anchored above the pill, and the list has to grow
  // downwards from the pill's other edge without stretching it.
  const groupPopover = canRegroup && groupOpen && anchor && (
    <div
      ref={popoverRef}
      data-testid="mindmap-groupby-popover"
      role="menu"
      aria-label={t("mindmap.groupBy")}
      onPointerEnter={openOverlay}
      onPointerLeave={closeOverlay}
      style={{
        position: "fixed",
        left: anchor.right,
        top: anchor.bottom + OVERLAY_OFFSET,
        transform: "translateX(-100%)",
        zIndex: 31,
        minWidth: 176,
        maxHeight: 240,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        padding: 4,
        borderRadius: 8,
        border: `1px solid ${isDark ? "#3d3d43" : "#e5e5e8"}`,
        background: isDark ? "#232329" : "#ffffff",
        color: isDark ? "#e8e8e8" : "#1b1b1f",
        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.18)",
        fontFamily: "var(--ui-font, inherit)",
        fontSize: 12,
        pointerEvents: "auto",
      }}
    >
      {groupOption(t("mindmap.groupNone"), "mindmap-groupby-none", undefined)}
      {groupings === null && !groupingFailed ? (
        <span style={{ padding: "6px 8px", opacity: 0.6 }}>
          {t("mindmap.groupingLoading")}
        </span>
      ) : groupings === null || groupings.length === 0 ? (
        <span style={{ padding: "6px 8px", opacity: 0.6 }}>
          {t("mindmap.groupingEmpty")}
        </span>
      ) : (
        groupings.map((grouping) =>
          groupOption(
            grouping.propertyName,
            `mindmap-groupby-${grouping.propertyName}`,
            grouping.propertyName,
          ),
        )
      )}
    </div>
  );

  return (
    <div
      className="mindmap-node"
      style={{ position: "relative", width: element.width, height }}
      onPointerEnter={openOverlay}
      onPointerLeave={closeOverlay}
    >
      {overlay && ReactDOM.createPortal(overlay, document.body)}
      {groupPopover && ReactDOM.createPortal(groupPopover, document.body)}
      <div
        ref={pillRef}
        data-testid="mindmap-node-pill"
        className="mindmap-node-pill"
        style={{
          position: "relative",
          width,
          height,
          boxSizing: "border-box",
          background: pillFill,
          border: `${MINDMAP_PILL_BORDER_WIDTH}px solid ${accentColor}`,
          borderRadius: Math.min(width, height) / 2,
          // A steady dim plate while the map is generating — see SPIN_STYLE_ID.
          opacity: isPlaceholder ? PLACEHOLDER_OPACITY : undefined,
        }}
      >
        <div
          data-testid="mindmap-node-body"
          style={{
            ...parentStyle,
            width: "100%",
            height: "100%",
            position: "relative",
            display: "flex",
            alignItems: "center",
            // The one place the pill's horizontal chrome is spent; every
            // estimate of the label box derives from these two constants via
            // `mindmapLabelBoxWidth`.
            gap: `${MINDMAP_PILL_GLYPH_GAP}px`,
            padding: `0 ${MINDMAP_PILL_PADDING}px`,
            boxSizing: "border-box",
            // Same key as `...parentStyle`'s themed `backgroundColor`. The
            // `background` shorthand cancels it only until a theme switch,
            // which then squares off the pill's rounded ends.
            backgroundColor: "transparent",
            color: getMindmapPillTextColor(isDark, isRoot),
            fontFamily: "var(--ui-font, inherit)",
            // The body is pure decoration: clicks and drags pass through to the
            // canvas so a pill drags like any other element. The hover overlay
            // and the chip are the only interactive parts (S9).
          }}
        >
          {isPlaceholder && (
            <span
              style={{
                ...glyphBoxStyle,
                // Sized like a glyph so the placeholder's label box matches the
                // real pill's and the text does not shift when it lands.
                display: "inline-flex",
              }}
            >
              <Spinner size={14} testId="mindmap-node-placeholder-spinner" />
            </span>
          )}
          {!isPlaceholder && element.nodeKind === "page" && (
            <span className="mindmap-node-glyph" style={glyphBoxStyle}>
              {NotionIcon}
            </span>
          )}
          {element.icon && (
            <span
              className="mindmap-node-icon"
              style={{ ...glyphBoxStyle, lineHeight: 1, overflow: "hidden" }}
            >
              {processNotionIcon(element.icon as NotionIconElementType)}
            </span>
          )}
          <span
            className="mindmap-node-label"
            style={{
              flex: "1 1 auto",
              overflow: "hidden",
              // Wrap, then ellipsis, at as many lines as this box is tall — so
              // resizing a pill TALLER reveals more text, not only wider.
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: mindmapPillClampLines(height),
              // `.notion-card-container` — every pill's ancestor — sets
              // `white-space: nowrap`, which inherits down here and makes
              // `-webkit-line-clamp` inert: the label stayed on one line and
              // ellipsized however much height the box had (V3 P3). Set
              // explicitly rather than relaxing the shared container rule, which
              // the notion-card renderer depends on.
              whiteSpace: "normal",
              lineHeight: `${MINDMAP_PILL_LINE_HEIGHT}px`,
              // Break BETWEEN words. `anywhere` let the browser split a word at
              // any glyph the moment the box was a pixel short, which is how a
              // five-letter "Dummy" rendered as "Dum / my"; `break-word` only
              // splits a token that cannot fit a line of its own at all.
              overflowWrap: "break-word",
              wordBreak: "normal",
            }}
          >
            {element.label}
          </span>
        </div>
      </div>
      {showsChip && (
        <button
          type="button"
          data-testid="mindmap-node-chip"
          data-direction={element.expanded ? "left" : "right"}
          aria-label={
            element.expanded
              ? t("mindmap.collapseNode")
              : t("mindmap.expandNode")
          }
          aria-busy={chipBusy || undefined}
          onClick={toggleExpand}
          onPointerDown={onChipPointerDown}
          style={{
            position: "absolute",
            // Seat the round chip in the reserved gap, just past the pill's
            // trailing edge, fully inside the element box (Fix 1).
            left: width + (chipGap - CHIP_SIZE) / 2,
            top: "50%",
            transform: "translateY(-50%)",
            width: CHIP_SIZE,
            height: CHIP_SIZE,
            borderRadius: "50%",
            border: "none",
            // Solid branch-colour disc with a white chevron: right = "show
            // children", left = "collapse". Reads at map zoom where the old
            // "<"/">" text glyphs turned to mush (V3).
            background: accentColor,
            color: "white",
            // A thin ring in the canvas tone separates the chip from the pill.
            boxShadow: `0 0 0 2px ${ringColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
            pointerEvents: "auto",
          }}
        >
          {chipBusy ? (
            <Spinner size={12} testId="mindmap-chip-spinner" />
          ) : (
            <ChipChevron direction={element.expanded ? "left" : "right"} />
          )}
        </button>
      )}
    </div>
  );
};

// One DOM node per mindmap element re-renders on every scroll/zoom frame; the
// props are stable object identities from App.tsx's memoized parentStyle (S5).
export const RenderMindmapNode = React.memo(RenderMindmapNodeInner);
